/**
 * Native-iOS-only stale-bundle guard.
 *
 * When the Kinsy iOS app runs, its WKWebView loads
 * `https://kinsy.io` (per `capacitor.config.ts` `server.url`).
 * Historically the site registered a service worker that cached
 * `index.html` and the main JS bundle, so on subsequent launches the
 * WKWebView happily served an OLD JS bundle from Cache Storage even
 * after we deployed a fresh production build. That was a
 * significant contributor to the "Build 43 still shows old
 * stale-timeout" symptom.
 *
 * This module, invoked ONCE per launch on native iOS ONLY:
 *   1. Fetches `/build-info.json` (with cache-busting).
 *   2. Reads the production bundle identity, preferring
 *        `bundle_id`  (main.js asset hash — always present when the
 *                      build/index.html was parseable, so this works
 *                      even when the CI build environment has no
 *                      access to a real Git SHA)
 *        `short_commit` (fallback)
 *        `commit`       (fallback)
 *   3. Compares against `localStorage.kinsyLastBundleId`.
 *   4. On a mismatch, unregisters every service worker, deletes every
 *      Cache Storage entry, and reloads the WebView ONCE. The identity
 *      is written to localStorage BEFORE the reload to guarantee no
 *      loop.
 *   5. On first launch (no stored identity), remembers the identity
 *      without reloading (nothing to be stale against yet).
 *   6. On a match, no-op.
 *
 * Never clears cookies, auth state, IndexedDB, or user content — only
 * the two SW / CacheStorage surfaces that hold stale JS. Failures are
 * silent (`try/catch`) so a network hiccup or an old iOS version
 * without `caches` support cannot brick the launch.
 */
import { isNative, isIOS } from './platform';

// New storage key. The previous `kinsyLastCommit` value from before we
// switched to bundle-hash identity is intentionally left untouched —
// on first launch after this deploy the guard will just re-seed
// `kinsyLastBundleId` with the current production identity and skip
// the reload, exactly like a fresh install.
const LAST_BUNDLE_ID_KEY = 'kinsyLastBundleId';
const RELOAD_GUARD_KEY   = 'kinsyBundleReloadInFlight';

function pickIdentity(info) {
  if (!info || typeof info !== 'object') return null;
  const candidate = info.bundle_id || info.short_commit || info.commit;
  if (!candidate || candidate === 'unknown') return null;
  return String(candidate);
}

export async function guardAgainstStaleBundle() {
  // Native iOS only — the web app doesn't have this WKWebView cache
  // problem and reloading in the middle of a user's browsing session
  // would be jarring.
  if (!(isNative() && isIOS())) return;

  try {
    const res = await fetch(`/build-info.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res || !res.ok) return;
    const info = await res.json();
    const productionId = pickIdentity(info);
    if (!productionId) return;

    const lastId = window.localStorage.getItem(LAST_BUNDLE_ID_KEY);

    // First launch on this device (or first launch after upgrading
    // past the old `kinsyLastCommit` key) — remember and move on.
    if (!lastId) {
      window.localStorage.setItem(LAST_BUNDLE_ID_KEY, productionId);
      return;
    }

    // Same bundle as last successful launch — nothing stale.
    if (lastId === productionId) return;

    // Reload-loop guard: if we already tried a reload for this new
    // identity and are STILL here, something is wrong upstream — do
    // not reload again. Update the stored identity + clear the flag
    // so future deploys can still trigger a clean reload.
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === productionId) {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
      window.localStorage.setItem(LAST_BUNDLE_ID_KEY, productionId);
      return;
    }

    // Identity changed — nuke the stale SW + Cache Storage, remember
    // the new identity, then reload ONCE.
    try {
      if (navigator.serviceWorker && typeof navigator.serviceWorker.getRegistrations === 'function') {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
    } catch (_) { /* ignore */ }
    try {
      if (typeof window.caches?.keys === 'function') {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k).catch(() => {})));
      }
    } catch (_) { /* ignore */ }

    // Mark the reload as in-flight for THIS specific identity so we
    // don't loop, and record the new identity as the current one.
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, productionId);
    window.localStorage.setItem(LAST_BUNDLE_ID_KEY, productionId);
    window.location.reload();
  } catch (_) {
    // Silent failure — a bad network at launch must not brick the app.
  }
}
